import Loading from "components/Loading"
import {
  FeatureEntry,
  FeatureFacet,
  FeatureFilters,
  hasFeatureFilters,
  toggleFeatureFilter,
} from "utils/featureFilters"
import { formatNumber } from "utils/projectDisplay"

interface Props {
  facets: FeatureFacet[]
  selectedFilters: FeatureFilters
  loading?: boolean
  error?: boolean
  resultCount?: number
  totalCount?: number
  truncated?: boolean
  onChange: (filters: FeatureFilters) => void
}

const CollectionFeatureFilters = ({
  facets,
  selectedFilters,
  loading = false,
  error = false,
  resultCount,
  totalCount,
  truncated = false,
  onChange,
}: Props) => {
  const hasFilters = hasFeatureFilters(selectedFilters)
  const activeFilters = Object.entries(selectedFilters).map(([name, value]) => ({ name, value }))

  if (loading && facets.length === 0) {
    return (
      <aside className="gallery-feature-panel">
        <h3 className="gallery-label text-gallery-muted mb-4">Features</h3>
        <Loading />
      </aside>
    )
  }

  if (error) {
    return (
      <aside className="gallery-feature-panel">
        <h3 className="gallery-label text-gallery-muted mb-4">Features</h3>
        <p className="text-sm text-red-500">Error loading feature filters.</p>
      </aside>
    )
  }

  if (facets.length === 0) return null

  const toggleFeature = (feature: FeatureEntry) => {
    onChange(toggleFeatureFilter(selectedFilters, feature))
  }

  return (
    <aside className="gallery-feature-panel" aria-label="Feature filters">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="gallery-label text-gallery-muted">Features</h3>
          {resultCount !== undefined && totalCount !== undefined && (
            <p className="mt-2 text-xs text-gallery-muted">
              {formatNumber(resultCount)} of {formatNumber(totalCount)}
              {truncated ? "+" : ""} works
            </p>
          )}
        </div>
        {hasFilters && (
          <button
            type="button"
            className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
            onClick={() => onChange({})}
          >
            Clear
          </button>
        )}
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {activeFilters.map((feature) => (
            <button
              key={`${feature.name}-${feature.value}`}
              type="button"
              className="gallery-feature-chip"
              onClick={() => toggleFeature(feature)}
            >
              {feature.name}: {feature.value} x
            </button>
          ))}
        </div>
      )}

      <div className="divide-y divide-gallery-border">
        {facets.map((facet) => (
          <details key={facet.name} className="gallery-feature-group" open={hasFilters && Boolean(selectedFilters[facet.name])}>
            <summary>
              <span>{facet.name}</span>
              <span>{selectedFilters[facet.name] || facet.values.length}</span>
            </summary>
            <div className="mt-3 space-y-1">
              {facet.values.map((value) => {
                const selected = selectedFilters[facet.name] === value.value
                return (
                  <button
                    key={value.value}
                    type="button"
                    aria-pressed={selected}
                    className="gallery-feature-option"
                    onClick={() => toggleFeature({ name: facet.name, value: value.value })}
                  >
                    <span>{value.value}</span>
                    <span>{formatNumber(value.count)}</span>
                  </button>
                )
              })}
            </div>
          </details>
        ))}
      </div>
    </aside>
  )
}

export default CollectionFeatureFilters
